// src/com/trucker_trans_plan/TruckerTransPlanItem.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, IconButton, Tooltip, Chip } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront'; // 동적표시용.
import LocationOnIcon from '@mui/icons-material/LocationOn'; // 지도로 이동
import LocationOffIcon from '@mui/icons-material/LocationOff'; // 위치정보 없음
import SearchIcon from '@mui/icons-material/Search'; // search vehicle
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; // set vehicle
import MessageIcon from '@mui/icons-material/Message'; // message
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { useAlertSetter } from '../../api/app-hook';
import {
    AppMenuUrl, AppObject, AppStyle, AppWord, InputType, MapValue, TopCode
} from '../../value/AppConst';
import {
    menuRequestRepo, menuTreeRepo, transPlanLaunchListParamRepo, userInfoRepo, vehInfoListOfDcRepo
} from '../../value/AppModel';
import TransPlanTsrEditorGroup, {TabCodeForTsrEditorGroup} from '../trans_plan_tsr/TransPlanTsrEditorGroup';
import { GET_TRANS_PLAN_TSR } from '../trans_plan_tsr/TransPlanTsrGql';
import { SEND_TRANS_PLAN_GUIDE_SMS } from '../trans_plan/TransPlanGql';
import ValueMisc from '../../value/ValueMisc';
import IsValid from '../../value/IsValid';
import { WhiteChip } from '../../common/VzmStyled';
import VzmSelector from '../../common/VzmSelector';
import AutoTextField from '../../common/AutoTextField';
import VehInfoSelectDialog from '../veh_info/VehInfoSelectDialog';

const HochaContainer = styled(Box)(({theme}) => ({
    display: 'inline-flex',
    width: 220,
    flexDirection: 'column',
    padding: theme.spacing(1),
    transition: 'all 0.3s ease',
    fontSize: '0.8rem',
    backgroundColor: '#f8f8f8',
    border: AppStyle.BorderEEE,
    margin: '5px 1px 1px 1px',
    '&:hover': {
        border: '1px solid ' + AppStyle.PrimaryRGB,
        borderRadius: 5,
        '& *': {
            transition: 'all 0.3s ease',
        },
        backgroundColor: '#ffb'
    }
}));

// store popover
const StoreLineBox = styled(Box)({display: 'flex', alignItems: 'center', padding: 5});

const TitleBox = styled(Box)({fontWeight: 'bold', textAlign: 'center', padding: 5});

const DataBox = styled(Box)({
    backgroundColor: 'white', padding: 5,
});

const LabelAndTextBox = styled(Box)({
    padding: '5px 0px 5px 5px', display: 'flex', alignItems: 'center', minHeight: 14
});
const LabelBox = styled(Box)({fontSize:'0.6rem'});
const TextBox = styled(Box)({flexGrow: 1, paddingRight: 5});

const LabelAndText = ({
    label,
    text,
    hover
}) => {
    return (
        <LabelAndTextBox>
            <TextBox>{text}</TextBox>
            <LabelBox style={{color:hover ? AppStyle.PrimaryRGB : 'transparent'}}>{label}</LabelBox>
        </LabelAndTextBox>
    )
};

const BlockHeight = 37;
const PlanInputBlock = styled(Box)({
    padding: '2px 0px 1px 0px', height: BlockHeight, boxSizing: 'border-box',
    display: 'flex', alignItems: 'center',
});

const UnderlinedLabel = styled(Box)({
    padding: '5px 0px 5px 14px', boxSizing: 'border-box', //backgroundColor: 'white',
    borderBottom: AppStyle.BorderCCC, display: 'flex', alignItems: 'center',
    flexGrow: 1,
});

const ComIdDef = {
    id: 'comId', label: '운송사',
    type: InputType.select, required: true, toAdd: true, toEdit: true,
    labelKey: 'comName',
};

const printDateTime = (time) => {
    if(time && time.length > 10) {
        return time.substring(5, 16).replace('-', '/');
    }
    return '';
}

const getVehicleObject = (vehInfoList, comId, vehPlates) => {
    if(vehPlates) {
        for(const veh of vehInfoList) {
            if(veh.vehPlates === vehPlates && veh.comId === comId) return veh;
        }
    }
    return null;
};

const EmptyVehInfoList = [{vehPlates:AppWord.ChooseKorean, driverName:'', driverMobile:''}];
const VehInfoSeqDict = {
    id: 'vehInfoSeq', label: '차량',
    type: InputType.select, required: false, toAdd: true, toEdit: true,
    labelKey: 'vehPlates',
};

/**
 * 홈플러스 배차등록 아이템.
 * @param {*} param0 
 * @returns 
 */
export default function TruckerTransPlanItem({
    itemIndex,
    item, // item of LIST_TRANS_PLAN_TSR. {hocha, planId, dcCd, dcName, storeId, storeName,... bulkItems:{}}
    comInfoList,
    holdMutation,
    onRequestSetTruck,
    onRequestOffTruck,
    onRequestSetCompany,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [inputSetVehicle, setInputSetVehicle] = useState({...AppObject.emptyInputData});
    const [vehicleSelected, setVehicleSelected] = useState({}); //지도이동 체크를 위해서.
    const menuTree = useReactiveVar(menuTreeRepo);
    const [hovered, setHovered] = useState(false);
    const [sentSmsToReady, setSentSmsToReady] = useState(false);
    const [editorState, setEditorState] = useState({open: false, tab: 0, data: null, changed: false});
    const [openVehicleSelectBox, setOpenVehicleSelectBox] = useState(false);
    const [goodMobile, setGoodMobile] = useState(false);
    const [daysOld, setDaysOld] = useState(0);
    const [storesBound, setStoresBound] = useState(false);
    const vehInfoListOfDcData = useReactiveVar(vehInfoListOfDcRepo);
    const [vehInfoList, setVehInfoList] = useState(EmptyVehInfoList);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const tabRef = useRef(0); // data 가져온 후 editor group 열 때 선택할 tab을 임시 저장.
    const [getTransPlanTsrItem, responseItem] = useLazyQuery(GET_TRANS_PLAN_TSR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetTransPlanTsrItem(data, option)},
        onError: (error) => addAlert({title: '배차정보 조회 오류', message: [error.message]})
    });
    const [sendGuideSms, responseSendMsg] = useMutation( SEND_TRANS_PLAN_GUIDE_SMS, {
        onCompleted: (data, option) => onCompleteSendGuideSms(data, option),
        onError: (error) => addAlert({title: "안내 문자 전송 오류", message: [error.message]})
    });

    useEffect(()=>{
        setEditorState({open: false, tab: 0, data: null, changed: false});
        const isGood = IsValid.mobile(item.driverMobile);
        setGoodMobile(isGood);
        const planDate = new Date(item.planDate);
        const today = new Date();
        setDaysOld(Math.floor((today - planDate) / 86400000));
        setStoresBound(item.bindStores && item.bindStores.length > 0);
        if(!isGood) setSentSmsToReady(true); // 전화번호가 잘못된 경우는 어차피 보낼 수 없으므로 true로 설정.
        setInput({comId: item.comId});

    }, [item]);

    const onChangeVehInfoList = (item, vehInfoList) => {
        const veh = vehInfoList.find((v) => v.vehPlates === item.vehPlates && v.comId === item.comId);
        if(veh) {
            setInputSetVehicle({
                data: {
                    vehPlates: item.vehPlates,
                    vehInfoSeq: veh.vehInfoSeq,
                    comId: item.comId,
                },
                changed: false,
                error: {},
                hasError: false
            });
        }
        else {
            setInputSetVehicle({
                data: {vehPlates: undefined, vehInfoSeq: 0, comId: undefined},
                changed: false,
                error: {},
                hasError: false
            });
        }
    };

    useEffect(()=>{
        onChangeVehInfoList(item, vehInfoList);
    }, [item, vehInfoList]);

    useEffect(() => {
        if(input && input.comId && vehInfoListOfDcData.records[input.comId]) {
            setVehInfoList(vehInfoListOfDcData.records[input.comId]);
        }
        else setVehInfoList(EmptyVehInfoList);
    }, [vehInfoListOfDcData, input]); // option list
    
    useEffect(() => {
        let veh = {};
        if(inputSetVehicle.data.vehPlates) {
            const vehFound = getVehicleObject(vehInfoList, input.comId, inputSetVehicle.data.vehPlates);
            if(vehFound) veh = vehFound;
        }
        setVehicleSelected(veh);
    }, [inputSetVehicle, input, vehInfoList]);

    /* ################### GQL callback ################### */
    const onCompleteSendGuideSms = (data, option) => {
        if(data.transPlanSendGuideSms.ok) {
            enqueueSnackbar('안내 문자 전송 완료', {variant: 'success', autoHideDuration: 1500});
            setSentSmsToReady(true);
        }
        else addAlert({title:"안내 문자 전송 오류", message:[data.transPlanSendGuideSms.message]});
    };

    const onCompleteGetTransPlanTsrItem = (data, option) => {
        if(data.transPlanTsrItem) {
            setEditorState({open:true, data:data.transPlanTsrItem, changed: false, tab: tabRef.current});
        }
        else addAlert({title:'배차정보 조회 오류', message:['배차정보를 찾을 수 없습니다.']});
    };

    const getTransPlanTsrItemOrOpenEditor = (tab) => {
        if(editorState.data) {
            // open editor
            setEditorState({...editorState, open: true, tab: tab});
        }
        else {
            // get data
            tabRef.current = tab;
            getTransPlanTsrItem({variables: {planId: item.planId}});
        }
    }

    const onClickGoToMap = () => {
        if(vehicleSelected.gpsTime) {
            // 지도로 이동
            const mapUrl = ValueMisc.makeMapUrl({
                job: MapValue.MapInitEvent.traceVehicle,
                /* vehInfoSeqToTrace:item.vehInfoSeq,
                vehPlates:item.vehPlates,
                vehAlias:item.vehPlates, */
                vehInfoSeqToTrace:vehicleSelected.vehInfoSeq,
                vehPlates:vehicleSelected.vehPlates,
                vehAlias:vehicleSelected.vehPlates
            });
            window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
        }
    };

    const onClickSetDetail = () => {
        getTransPlanTsrItemOrOpenEditor(TabCodeForTsrEditorGroup.DetailEditor);
    };

    const onClickSetSupplies = () => {
        getTransPlanTsrItemOrOpenEditor(TabCodeForTsrEditorGroup.SupplyEditor);
    };

    // 상차 상세 내역이나 물류집기 변경 요청
    const onRequestSaveInMemory = (data) => {
        const newState = {...editorState, data: data, changed: true, open: false};
        setEditorState(newState);
    };

    // 배차요청 화면으로 이동하기.
    const onClickToMoveToTransPlanRequest = () => {
        if(sessionInfo.isHomeplusOrEtrace()) {
            const menuSet = ValueMisc.findMenu(AppMenuUrl.TransPlan, menuTree);
            if(!menuSet.menu) {
                addAlert({
                    title: '운송요청 화면 이동',
                    message: ['운송요청 화면이 없습니다. 관리자에게 문의해 주세요.'],
                });
                return;
            }
            transPlanLaunchListParamRepo({
                planDate: item.planDate,
                storeId: item.storeId,
            })
            menuRequestRepo(menuSet);
        }
    };

    const onRequestCloseGroupEditor = ()=> {
        const newState = {...editorState, open: false};
        setEditorState(newState);
    }

    const onChangeCompany = (id, value) => {
        //onChangeValue(id, value === AppWord.ChooseValue ? undefined : value, false);
        if(value !== input.comId) {
            const newInput = {...input};
            newInput.comId = value;
            if(value === item.comId) onChangeVehInfoList(item, vehInfoList);
            else onVehicleSelected(VehInfoSeqDict.id, 0);

            setInput(newInput);
        }
    };

    const onVehicleSelected = (id, value) => {
        const newData = {...inputSetVehicle};
        if(newData.data.vehInfoSeq !== value) {
            // newData.data.vehPlates = value===0 ? undefined : value;
            const veh = vehInfoList.find((v) => v.vehInfoSeq === value);
            if(veh) {
                newData.data.vehInfoSeq = value;
                newData.data.vehPlates = veh.vehPlates;
                newData.data.comId = veh.comId;
                newData.changed = true;
            }
            else {
                newData.data.vehInfoSeq = 0;
                newData.data.vehPlates = undefined;
                newData.data.comId = undefined;
                newData.changed = true;
            }
            setInputSetVehicle(newData);
        }
    };
    
    const onChangeVehicleInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            // 기존 값을 다시 넣어줘야 하는지 고민.
            const newData = {...inputSetVehicle};
            newData.data.vehPlates = undefined;
            newData.data.vehInfoSeq = 0;
            newData.data.comId = undefined;
            newData.changed = true;
            setInputSetVehicle(newData);
        }
    };

    const onClickSetCompany = async () => {
        if(item.comId !== input.comId) {
            const confirm = await openConfirmDialog({
                title: "운송사 변경",
                content: [
                    `${item.hocha}호차의 운송사를 변경합니다. ${item.comId} -> ${input.comId}`,
                    '계속하시겠습니까?'
                ]
            });

            if(confirm) {
                const param = {planId: item.planId, comId: input.comId};
                onRequestSetCompany(param);
            }
        }
    };

    const onClickSetVehicle = () => {
        if(item && item.planId && vehicleSelected.vehPlates && input.comId) {
            const param = {
                planId: item.planId,
                vehPlates: vehicleSelected.vehPlates,
                comId: input.comId,
            };
            onRequestSetTruck(param);
        }
    };

    const onClickRemoveVehicle = async () => {
        if(item && item.vehPlates) {
            const confirm =  await openConfirmDialog({
                title: "배차 취소",
                content: [
                    `${item.hocha}호차의 ${item.vehPlates} 차량 배차를 취소합니다.`,
                    '계속하시겠습니까?'
                ]
            });

            if(confirm) {
                onRequestOffTruck(item.planId);
            }
        }
    };

    const onClickSendGuideSms = async () => {
        if(item && item.vehPlates && item.driverMobile && item.reqStateFlag !== TopCode.ReqStateFlag.TsrIssued) {
            const confirm =  await openConfirmDialog({
                title: "안내 문자 전송",
                content: [
                    `${item.hocha}호차의 ${item.vehPlates} 차량에 안내 문자를 전송합니다.`,
                    '계속하시겠습니까?'
                ]
            });

            if(confirm) {
                sendGuideSms({variables: {planId: item.planId}});
            }
            else setSentSmsToReady(true); // 실제는 보내지 않았으나 거부한 경우도 보낸 것으로 처리.
        }
    };


    const renderBindStores = () => {
        const stores = item.bindStores;
        if(stores && stores.length > 0) {
            return (
                <Box style={{padding:5}}
                >
                    <Tooltip
                        title={
                            <Box>
                                {
                                    stores.map((store) => {
                                        return (
                                            <StoreLineBox key={store.storeId}
                                                style={{fontSize:'0.9rem'}}
                                            >
                                                <StorefrontIcon
                                                    fontSize={AppWord.SMALL}
                                                    color={
                                                        store.storeId === item.storeId
                                                        ? AppStyle.mui.inherit
                                                        : AppStyle.mui.warning
                                                    }
                                                />
                                                <Box style={{paddingLeft: 5}}>
                                                    [{store.storeId}] {store.storeName}
                                                </Box>
                                            </StoreLineBox>
                                        )
                                    })
                                }
                            </Box>
                        }
                    >
                        <Box style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            {
                                stores.map((store)=>{
                                    return (
                                        <React.Fragment key={store.storeId}> ..
                                        <StorefrontIcon fontSize={AppWord.SMALL}
                                            color={
                                                store.storeId === item.storeId
                                                ? AppStyle.mui.inherit
                                                : AppStyle.mui.warning
                                            }
                                        />
                                        </React.Fragment>
                                    )
                                })
                            }
                        </Box>
                    </Tooltip>
                </Box>
            );
        }
        return (
            <Box style={{padding:5, display:'flex', alignItems:'center'}}>
                <StorefrontIcon fontSize={AppWord.SMALL} color={AppStyle.mui.disabled} /> 동적 점포 없음
            </Box>
        )
    };

    const loadingAny
        = responseItem.loading
        || responseSendMsg.loading;

    return (
        <HochaContainer
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <TitleBox
                style={{color: hovered ? AppStyle.PrimaryRGB : 'black'}}
            >
                <Tooltip title={item.planId}>
                    <WhiteChip
                        label={item.hocha+"호차"}
                        size={AppWord.SMALL}
                    />
                </Tooltip>
                {
                    item.reqStateFlag === TopCode.ReqStateFlag.TsrNotIssued ?
                    <Tooltip title={
                        sessionInfo.isHomeplusOrEtrace()
                        ? "운송요청으로 이동" : "TSR이 아직 발행되지 않았습니다."}>
                        <Chip
                            label="TSR미발행"
                            size={AppWord.SMALL}
                            onClick={onClickToMoveToTransPlanRequest}
                            color={AppStyle.mui.warning}
                            style={{marginLeft: 5, fontSize:'0.7rem', fontWeight:'normal'}}
                        />
                    </Tooltip>
                    :
                    item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued ?
                    <Chip
                        label="TSR발행"
                        size={AppWord.SMALL}
                        onClick={onClickToMoveToTransPlanRequest}
                        color={AppStyle.mui.success}
                        style={{marginLeft: 5, fontSize:'0.7rem', fontWeight:'normal'}}
                    />
                    : null
                }
                {
                    storesBound &&
                    <Chip label="동" size={AppWord.SMALL}
                        color={AppStyle.mui.error}
                        style={{marginLeft: 5, fontSize:'0.7rem', fontWeight:'normal'}}
                    />
                }
            </TitleBox>
            <DataBox>
                <LabelAndText label="배차구분" text={item.transType || '-'} hover={hovered} />
                <LabelAndText label="차종" text={item.vehTypeName} hover={hovered} />
                {
                    item.reqStateFlag === TopCode.ReqStateFlag.NewRequest
                    ?
                    <Box style={{paddingLeft: 5}}>
                        <VzmSelector
                            dict={ComIdDef}
                            value={input.comId || AppWord.ChooseValue}
                            onChange={onChangeCompany}
                            list={comInfoList}
                            fullWidth={true}
                            variant={AppStyle.mui.standard}
                            hideLabel={true}
                        />
                    </Box>
                    :
                    <LabelAndText label="운송사" text={item.comName} hover={hovered} />
                }
                <Button
                    disabled={loadingAny || !input.comId || input.comId === item.comId || input.comId === AppWord.ChooseValue || item.reqStateFlag !== TopCode.ReqStateFlag.NewRequest}
                    size={AppWord.SMALL}
                    variant={AppStyle.mui.contained}
                    fullWidth={true}
                    color={AppStyle.mui.secondary}
                    style={{marginTop: 3, marginBottom:3}}
                    onClick={onClickSetCompany}
                >
                    운송사 변경 저장
                </Button>
                <LabelAndText label="접안시간" text={printDateTime(item.shprReqTime)} hover={hovered} />
                <LabelAndText label="출발요청시간" text={printDateTime(item.dptrReqTime)} hover={hovered} />
                <LabelAndText label="도착요청시간" text={printDateTime(item.arrReqTime)} hover={hovered} />
                <LabelAndText label="Dock No." text={item.dockno || '-'} hover={hovered} />
                <LabelAndText label="추가운임" text={item.addCharge || '-'} hover={hovered} />
                <LabelBox style={{color:'#777', marginTop:5}}>비고</LabelBox>
                <Box
                    style={{
                        padding:5, border: AppStyle.BorderEEE, borderRadius: 5, height: 40,
                        overflow: 'auto', whiteSpace: 'pre-wrap'
                    }}
                >
                    {item.memo}
                </Box>
                {renderBindStores()}
            </DataBox>
            <Box
                style={{
                    padding: 5,
                    backgroundColor: '#cef',
                    borderRadius: 5,
                }}
            >
                <PlanInputBlock>
                    <AutoTextField
                        dict={VehInfoSeqDict}
                        value={
                            item && inputSetVehicle.data.vehPlates && vehInfoList.length > 1
                            ? inputSetVehicle.data.vehInfoSeq
                            : 0
                        }
                        onChange={onVehicleSelected}
                        onInputChange={onChangeVehicleInputText}
                        list={item ? vehInfoList : EmptyVehInfoList}
                        style={{minWidth:180}}
                        hideLabel={true}
                    />
                    <IconButton
                        color={AppStyle.mui.primary}
                        size={AppWord.SMALL}
                        disabled={!item || loadingAny}
                        onClick={()=>setOpenVehicleSelectBox(true)}
                    >
                        <SearchIcon />
                    </IconButton>
                </PlanInputBlock>
                <PlanInputBlock>
                    <UnderlinedLabel>
                        {
                            vehicleSelected.driverName
                            || <span style={{color:'#ccc'}}>[없음]</span>
                        }
                    </UnderlinedLabel>
                    {
                        vehicleSelected.vehPlates &&
                        <Tooltip title={vehicleSelected.gpsTime ? "지도로 이동" : "위치정보 없음"}>
                            <span>
                                <IconButton
                                    size={AppWord.SMALL}
                                    onClick={onClickGoToMap}
                                    color={AppStyle.mui.secondary}
                                    disabled={!vehicleSelected.gpsTime}
                                >
                                    {
                                        vehicleSelected.gpsTime ?
                                        <LocationOnIcon fontSize='11pt' /> :
                                        <LocationOffIcon fontSize='11pt' />
                                    }
                                </IconButton>
                            </span>
                        </Tooltip>
                    }
                </PlanInputBlock>
                <PlanInputBlock>
                    <UnderlinedLabel>
                        {
                            vehicleSelected.driverMobile ||
                            <span style={{color:'#ccc'}}>[없음]</span>
                        }
                    </UnderlinedLabel>
                    <Tooltip title="차량배정 취소">
                        <span>
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.warning}
                                size={AppWord.SMALL}
                                disabled={!inputSetVehicle.data.vehPlates || !item || !item.vehPlates || holdMutation}
                                onClick={onClickRemoveVehicle}
                            >
                                초기화
                            </Button>
                        </span>
                    </Tooltip>
                </PlanInputBlock>
                <Box style={{display:'flex', marginTop:5}}>
                    <Button
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                        size={AppWord.SMALL}
                        style={{flexGrow:1, marginRight:2}}
                        startIcon={<LocalShippingIcon />}
                        disabled={!inputSetVehicle.changed || !item || holdMutation}
                        onClick={onClickSetVehicle}
                    >
                        등록
                    </Button>
                    <Button
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.inherit}
                        size={AppWord.SMALL}
                        style={{flexGrow:1, marginLeft:2}}
                        startIcon={<MessageIcon />}
                        disabled={
                            !Boolean(item) || inputSetVehicle.changed
                            || !item.vehPlates || !item.driverMobile || !goodMobile
                            || item.reqStateFlag===TopCode.ReqStateFlag.TsrIssued
                            || vehicleSelected.comId !== item.comId
                            || responseSendMsg.loading || holdMutation}
                        onClick={onClickSendGuideSms}
                    >
                        메시지
                    </Button>
                </Box>
            </Box>
            <Button
                style={{marginTop: 5}}
                size={AppWord.SMALL}
                variant={AppStyle.mui.outlined}
                color={AppStyle.mui.primary}
                disabled={loadingAny}
                onClick={onClickSetDetail}
            >
                상차 상세 내역
            </Button>
            <Button
                style={{marginTop: 5}}
                size={AppWord.SMALL}
                variant={AppStyle.mui.outlined}
                color={AppStyle.mui.secondary}
                disabled={loadingAny }
                onClick={onClickSetSupplies}
            >
                물류집기
            </Button>
            {
                editorState.open && editorState.data &&
                <TransPlanTsrEditorGroup
                    open={editorState.open}
                    item={editorState.data}
                    tabSelected={editorState.tab}
                    onClose={onRequestCloseGroupEditor}
                    onRequestSave={onRequestSaveInMemory}
                />
            }
            <VehInfoSelectDialog
                open={openVehicleSelectBox}
                onClose={()=>setOpenVehicleSelectBox(false)}
                records={vehInfoList.slice(1)}
                onSelect={(veh) => {
                    onVehicleSelected(VehInfoSeqDict.id, veh.vehInfoSeq);
                    setOpenVehicleSelectBox(false);
                }}
                defaultSearchText={inputSetVehicle.data.vehPlates}
            />
        </HochaContainer>
    )
}